import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config/api';
import { ShoppingCart, ChevronRight, X, Plus, Minus } from 'lucide-react';
import { Container, Section } from '../components/UIComponents';
import { Button } from '../components/Button';
import ShippingCalculator from '../components/ShippingCalculator';
import { getCartItemTotal } from '../utils/pricing';

const HeroSection = () => {
  return (
    <section className="bg-gradient-to-br from-slate-50 via-teal-50 to-blue-50 pt-8 md:pt-16 pb-16 md:pb-28">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Shopping Cart</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">Review your items and proceed to checkout</p>
        </motion.div>
      </Container>
    </section>
  );
};

export default function Cart() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shippingCost, setShippingCost] = useState(0);
  const [shippingMethod, setShippingMethod] = useState('');
  const [estimatedDelivery, setEstimatedDelivery] = useState(null);

  useEffect(() => {
    if (token) {
      fetchCart();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCart = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cart`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      setCartItems(data.data || []);
    } catch (err) {
      console.error('Error fetching cart:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (cartItemId, newQuantity) => {
    if (!token) return;

    // If quantity becomes 0 or less, remove the item
    if (newQuantity < 1) {
      removeItem(cartItemId);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/cart/${cartItemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ quantity: newQuantity }),
      });

      if (response.ok) {
        // Update state immediately (optimistic update)
        setCartItems(cartItems.map(item =>
          item.id === cartItemId ? { ...item, quantity: newQuantity } : item
        ));
      }
    } catch (err) {
      console.error('Error updating quantity:', err);
    }
  };

  const removeItem = async (cartItemId) => {
    if (!token) return;

    // Remove from UI immediately (optimistic update)
    const itemToRemove = cartItems.find(item => item.id === cartItemId);
    setCartItems(cartItems.filter(item => item.id !== cartItemId));

    // Dispatch custom event to update NavbarNew cart count
    window.dispatchEvent(new Event('cartUpdated'));

    // Delete from backend
    try {
      await fetch(`${API_BASE_URL}/api/cart/${cartItemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
    } catch (err) {
      console.error('Error removing item:', err);
    }
  };

  const handleShippingUpdate = (shippingInfo) => {
    setShippingCost(Number(shippingInfo.shippingCost) || 0);
    setShippingMethod(shippingInfo.method);
    setEstimatedDelivery(shippingInfo.estimatedDelivery);
  };

  const subtotal = cartItems.reduce((sum, item) => {
    return sum + getCartItemTotal(item).total;
  }, 0);

  const gst = Math.round(subtotal * 0.05);
  const total = Math.round(subtotal + gst + (Number(shippingCost) || 0));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-teal-50">
        <motion.div
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-center"
        >
          <div className="text-6xl mb-4">📦</div>
          <p className="text-slate-600 font-semibold">Loading cart...</p>
        </motion.div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="w-full bg-white">
        <HeroSection />
        <Section className="bg-white">
          <Container>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <div className="text-6xl mb-6">🔐</div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Login Required</h2>
              <p className="text-slate-600 mb-8 max-w-2xl mx-auto">Please login to view your cart</p>
              <div className="flex gap-4 justify-center">
                <Button size="lg" variant="primary" href="/user-login">Login</Button>
                <Button size="lg" variant="secondary" href="/user-register">Sign Up</Button>
              </div>
            </motion.div>
          </Container>
        </Section>
      </div>
    );
  }

  return (
    <div className="w-full bg-white">
      <HeroSection />

      <Section className="bg-white">
        <Container>
          {cartItems.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20"
            >
              <div className="text-6xl mb-6">🛒</div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Cart is Empty</h2>
              <p className="text-slate-600 mb-8">Add some healthy products to get started</p>
              <Button
                size="lg"
                variant="primary"
                href="/products"
                className="inline-flex items-center gap-2"
              >
                Continue Shopping
                <ChevronRight size={20} />
              </Button>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <motion.div className="lg:col-span-2">
                <div className="bg-slate-50 rounded-lg border border-slate-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-teal-50 to-blue-50 px-6 py-4 border-b border-slate-200">
                    <h2 className="text-xl font-bold text-slate-900">
                      {cartItems.length} Item{cartItems.length !== 1 ? 's' : ''}
                    </h2>
                  </div>

                  <AnimatePresence>
                    {cartItems.map((item, index) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        className={`p-4 md:p-6 flex gap-4 md:gap-6 ${index !== cartItems.length - 1 ? 'border-b border-slate-200' : ''}`}
                      >
                        {/* Product Image */}
                        <div className="flex-shrink-0">
                          <motion.div
                            animate={{ scale: [1, 1.05, 1] }}
                            transition={{ duration: 3, repeat: Infinity, delay: index * 0.2 }}
                            className="text-4xl md:text-5xl w-16 md:w-20 flex items-center justify-center"
                          >
                            📦
                          </motion.div>
                        </div>

                        {/* Product Details */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-slate-900 text-lg mb-1 truncate">{item.name}</h3>
                          <p className="text-sm text-slate-600 mb-3">
                            {!item.hasNoSizeSelection && item.size_name && <span>Size: {item.size_name}</span>}
                            {item.side && <span> — {item.side === 'right' ? 'Right' : 'Left'} side</span>}
                          </p>

                          {/* Price */}
                          {(() => {
                            const { mrp, unitPrice, itemDiscount, offerApplied } = getCartItemTotal(item);
                            return (
                              <div className="flex items-center gap-3 mb-4 flex-wrap">
                                {itemDiscount > 0 && (
                                  <span className="line-through text-slate-400">₹{mrp}</span>
                                )}
                                <span className="text-lg font-bold text-teal-600">
                                  ₹{unitPrice}
                                </span>
                                {itemDiscount > 0 && (
                                  <span className="text-sm font-bold text-red-500">-{Math.round(itemDiscount)}%</span>
                                )}
                                {offerApplied && (
                                  <span className="text-xs font-bold text-white bg-green-600 px-2 py-1 rounded-full">
                                    {offerApplied.label || `Extra ${offerApplied.effectiveDiscount || offerApplied.discountPercent}% off on ${offerApplied.minQty}+`}
                                  </span>
                                )}
                              </div>
                            );
                          })()}

                          {/* Quantity Controls */}
                          <div className="flex items-center gap-3">
                            <div className="flex items-center border border-slate-300 rounded-lg overflow-hidden bg-white">
                              <motion.button
                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                whileTap={{ scale: 0.95 }}
                                className="p-2 hover:bg-slate-100 transition-colors"
                              >
                                <Minus size={16} className="text-slate-600" />
                              </motion.button>
                              <span className="px-4 font-semibold text-slate-900">{item.quantity}</span>
                              <motion.button
                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                whileTap={{ scale: 0.95 }}
                                className="p-2 hover:bg-slate-100 transition-colors"
                              >
                                <Plus size={16} className="text-slate-600" />
                              </motion.button>
                            </div>

                            <motion.button
                              onClick={() => removeItem(item.id)}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="ml-auto px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium text-sm"
                            >
                              Remove
                            </motion.button>
                          </div>
                        </div>

                        {/* Item Total */}
                        <div className="text-right">
                          <p className="text-sm text-slate-600 mb-2">Subtotal</p>
                          <p className="text-xl md:text-2xl font-bold text-teal-600">
                            ₹{getCartItemTotal(item).total}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>

                {/* Continue Shopping */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mt-6"
                >
                  <Link
                    to="/products"
                    className="text-teal-600 hover:text-teal-700 font-semibold flex items-center gap-2 transition-colors"
                  >
                    ← Continue Shopping
                  </Link>
                </motion.div>
              </motion.div>

              {/* Order Summary Sidebar */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="lg:col-span-1"
              >
                <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-lg border border-slate-200 p-6 sticky top-24">
                  <h3 className="text-xl font-bold text-slate-900 mb-6">Order Summary</h3>

                  {/* Shipping Calculator */}
                  <div className="mb-6 pb-6 border-b border-slate-200">
                    <ShippingCalculator 
                      cartTotal={Math.round(subtotal + gst)} 
                      onShippingUpdate={handleShippingUpdate}
                    />
                  </div>

                  <div className="space-y-4 mb-6 pb-6 border-b border-slate-200">
                    <div className="flex justify-between text-slate-600">
                      <span>Subtotal</span>
                      <span className="font-semibold">₹{Math.round(subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-slate-600">
                      <span>GST (5%)</span>
                      <span className="font-semibold">₹{gst}</span>
                    </div>
                    {shippingMethod && (
                      <div className="flex justify-between text-slate-600">
                        <span>Shipping ({shippingMethod})</span>
                        <span className="font-semibold text-teal-600">₹{(Number(shippingCost) || 0).toFixed(2)}</span>
                      </div>
                    )}
                    {shippingMethod && estimatedDelivery && (
                      <div className="flex justify-between text-sm text-slate-500">
                        <span>📅 Estimated Delivery</span>
                        <span>{new Date(estimatedDelivery).toLocaleDateString('en-IN', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric' 
                        })}</span>
                      </div>
                    )}
                    {!shippingMethod && (
                      <div className="text-sm text-orange-600 font-semibold">
                        ⚠️ Select a shipping method above
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between mb-6 pb-6 border-b border-slate-200">
                    <span className="font-bold text-slate-900">Total</span>
                    <span className="text-2xl font-bold text-teal-600">₹{total}</span>
                  </div>

                  <motion.button
                    onClick={() => navigate('/checkout', { state: { shippingCost, shippingMethod, estimatedDelivery } })}
                    disabled={!shippingMethod}
                    whileHover={shippingMethod ? { scale: 1.02 } : {}}
                    whileTap={shippingMethod ? { scale: 0.98 } : {}}
                    className={`w-full ${
                      shippingMethod
                        ? 'bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800'
                        : 'bg-gray-400 cursor-not-allowed'
                    } text-white font-bold py-3 px-4 rounded-lg transition-all flex items-center justify-center gap-2 mb-4`}
                  >
                    <ShoppingCart size={20} />
                    {shippingMethod ? 'Proceed to Checkout' : 'Select Shipping First'}
                  </motion.button>

                  <motion.button
                    onClick={() => navigate('/products')}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-slate-200 hover:bg-slate-300 text-slate-900 font-bold py-3 px-4 rounded-lg transition-colors"
                  >
                    Continue Shopping
                  </motion.button>
                </div>
              </motion.div>
            </div>
          )}
        </Container>
      </Section>
    </div>
  );
}
